import React, { useState, useEffect } from "react";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import CustomButton from "./CustomButton";

export default function EmployeeTable() {
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];

  const formatDateForQuery = (d) => {
    if (!d) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) {
      const [y, m, day] = d.split("-");
      return `${String(day).padStart(2, "0")}/${String(m).padStart(
        2,
        "0"
      )}/${y}`;
    }
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(d)) return d;
    if (/^\d{2}\/[A-Za-z]{3}\/\d{4}$/.test(d)) {
      const [day, mon, year] = d.split("/");
      const mIndex = monthNames.indexOf(mon) + 1;
      return `${String(day).padStart(2, "0")}/${String(mIndex).padStart(
        2,
        "0"
      )}/${year}`;
    }
    const dt = new Date(d);
    if (!isNaN(dt.getTime())) {
      const day = String(dt.getDate()).padStart(2, "0");
      const mon = String(dt.getMonth() + 1).padStart(2, "0");
      const year = dt.getFullYear();
      return `${day}/${mon}/${year}`;
    }
    return d;
  };

  const formatDateForDisplay = (dateString) => {
    if (!dateString) return "";
    try {
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString))
        return new Date(dateString).toLocaleDateString();
      if (/^\d{2}\/\d{2}\/\d{4}$/.test(dateString)) {
        const [day, mon, year] = dateString.split("/");
        return new Date(`${year}-${mon}-${day}`).toLocaleDateString();
      }
      if (/^\d{2}\/[A-Za-z]{3}\/\d{4}$/.test(dateString)) {
        const [day, mon, year] = dateString.split("/");
        const mIndex = monthNames.indexOf(mon) + 1;
        return new Date(
          `${year}-${String(mIndex).padStart(2, "0")}-${day}`
        ).toLocaleDateString();
      }
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const formatDateTimeForDisplay = (dateString) => {
    if (!dateString) return "";
    try {
      const dt = new Date(dateString);
      if (isNaN(dt.getTime())) return dateString;
      return dt.toLocaleString();
    } catch {
      return dateString;
    }
  };

  const formatCharge = (val) => {
    if (val === null || val === undefined || val === "") return "-";
    if (typeof val === "boolean") return val ? "Yes" : "No";
    if (!isNaN(Number(val))) return String(val);
    if (typeof val === "string") {
      const low = val.toLowerCase();
      if (low === "true" || low === "yes") return "Yes";
      if (low === "false" || low === "no") return "No";
      return val;
    }
    return String(val);
  };

  const todayIso = new Date().toISOString().split("T")[0];

  const [stateFilter, setStateFilter] = useState("");
  const [divisionFilter, setDivisionFilter] = useState("");
  const [workingDateIso, setWorkingDateIso] = useState(todayIso);

  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingDivisions, setLoadingDivisions] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  const normalizeStates = (raw) =>
    Array.isArray(raw)
      ? raw.map((s, idx) => {
          if (typeof s === "string") return { id: idx, name: s, value: s };
          const id = s.id ?? s.state_id ?? s.stateId ?? idx;
          const name = s.name ?? s.state_name ?? s.state ?? String(s);
          return {
            id,
            name,
            value: typeof name === "string" ? name : String(name),
          };
        })
      : [];

  const normalizeDivisions = (raw) =>
    Array.isArray(raw)
      ? raw.map((d, idx) => {
          if (typeof d === "string") return { id: idx, name: d, value: d };
          const id = d.id ?? d.division_id ?? idx;
          const name = d.name ?? d.division_name ?? d.division ?? String(d);
          return {
            id,
            name,
            value: typeof name === "string" ? name : String(name),
          };
        })
      : [];

  useEffect(() => {
    let mounted = true;
    setLoadingStates(true);
    axios
      .get(`${BASE_URL}/api/states`, { headers: getAuthHeaders() })
      .then((res) => {
        if (!mounted) return;
        const normalized = normalizeStates(res.data || []);
        setStates(normalized);
        if (normalized.length > 0) setStateFilter(normalized[0].value);
      })
      .finally(() => setLoadingStates(false));
    return () => (mounted = false);
  }, []);

  useEffect(() => {
    if (!stateFilter) return setDivisionFilter(""), setDivisions([]);
    const st = states.find((s) => s.value === stateFilter);
    setLoadingDivisions(true);
    axios
      .get(`${BASE_URL}/api/divisions?state_id=${st?.id}`, {
        headers: getAuthHeaders(),
      })
      .then((res) => {
        const normalized = normalizeDivisions(res.data || []);
        setDivisions(normalized);
        setDivisionFilter(normalized.length > 0 ? normalized[0].value : "");
      })
      .finally(() => setLoadingDivisions(false));
  }, [stateFilter, states]);

  const handleGo = async () => {
    if (!stateFilter) return;

    const st = states.find((s) => s.value === stateFilter);
    setLoadingEmployees(true);

    try {
      const params = new URLSearchParams();
      params.set("state_id", st.id);
      if (divisionFilter) params.set("division", divisionFilter);
      if (workingDateIso)
        params.set("working_date", formatDateForQuery(workingDateIso));

      const url = `${BASE_URL}/api/pob-targets/pob_target_report?${params.toString()}`;
      const res = await axios.get(url, { headers: getAuthHeaders() });

      const raw = Array.isArray(res.data) ? res.data : res.data?.data ?? [];

      setEmployees(
        raw.map((e, idx) => {
          const employeeCode = e.employee ?? e.emp_code ?? "";
          const employeeDbId = e.employee_id ?? e.emp_id ?? "";

          
          const name =
            e.name ??
            e.employee_name ??
            `${e.first_name ?? ""} ${e.last_name ?? ""}`.trim();
          const mobileLogin = e.last_login_time ?? e.first_call_time ?? null;
          const mobileLogout = e.last_logout_time ?? e.last_call_time ?? null;
          return {
             sno: e.sno ?? idx + 1,
             bid: employeeDbId,
             employee: employeeCode,
             name,
            uerCharging: e.uer_charging ?? null,
            latest_battery_level: e.latest_battery_level ?? null,
            charging: e.charging ?? null,
            usb_charging: e.usb_charging ?? null,
            ac_charging: e.ac_charging ?? null,
            deviceId: e.device_id ?? e.registered_device_id ?? "",
            designation: e.designation ?? "",
            final_joining_date: e.final_joining_date ?? "",
            phone: e.phone_no ?? "",
            state: e.state ?? stateFilter,
            hq: e.headquarter ?? "",
            division: e.division ?? divisionFilter,
            manager: e.manager ?? "",
            mobileLogin,
            mobileLogout,
          };
        })
      );
    } finally {
      setLoadingEmployees(false);
    }
  };

  const openBatteryStatsForEmployee = (employee) => {
    const wd = formatDateForQuery(workingDateIso || todayIso);
    const params = new URLSearchParams();
    if (wd) params.set("working_date", wd);
    if (employee.bid) params.set("employee_id", employee.bid);
    if (stateFilter) params.set("state", stateFilter);
    if (divisionFilter) params.set("division", divisionFilter);

    const url = `${
      window.location.origin
    }/admin/Battery-stats?${params.toString()}`;
    window.open(url, "_blank");
  };

  const handlePrint = () => {
    const content = document.querySelector(".employee-table-wrapper");
    const printWindow = window.open("", "", "width=1200,height=900");

    printWindow.document.write(`
      <html><head><title>Print</title>
      <style>
        table { width: 100%; border-collapse: collapse; }
        th,td { border:1px solid #000; padding:6px; }
        thead { background:#0A66C2; color:white; font-weight:bold; }
        tr:nth-child(even) { background:#f2f2f2; }
      </style>
      </head><body>${content.innerHTML}</body></html>
    `);

    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 300);
  };

  const handleExport = () => {
    if (employees.length === 0) return alert("No data to export");

    const header = [
      "S.No",
      "Emp Id",
      "Employee",
      "Working Date",
      "Mobile Login",
      "Mobile Logout",
      "Latest Level",
      "Charging",
      "USB Charging",
      "AC Charging",
      "UER Charging",
      "Device ID",
      "Designation",
      "DOJ",
      "Mobile",
      "State",
      "HQ",
      "Division",
      "Manager",
    ];

    const rows = employees.map((r) => [
      r.sno,
      r.employee,
      r.name,
      formatDateForQuery(workingDateIso) || "",
      r.mobileLogin ? formatDateTimeForDisplay(r.mobileLogin) : "",
      r.mobileLogout ? formatDateTimeForDisplay(r.mobileLogout) : "",
      r.latest_battery_level ?? "",
      formatCharge(r.charging),
      formatCharge(r.usb_charging),
      formatCharge(r.ac_charging),
      formatCharge(r.uerCharging),
      r.deviceId,
      r.designation,
      r.final_joining_date ? formatDateForDisplay(r.final_joining_date) : "",
      r.phone,
      r.state,
      r.hq,
      r.division,
      r.manager,
    ]);

    const csvContent = [header, ...rows]
      .map((e) => e.map((x) => `"${String(x).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = url;
    a.download = `Employee_Report_${stateFilter}_${divisionFilter}_${workingDateIso}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const [page, setPage] = useState(1);
  const rowsPerPage = 50;
  const paginatedRows = employees.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  return (
    <div className="p-2 border-2 border-gray-300 rounded-lg bg-white">
      <div className="p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
        <div className="flex items-center justify-center p-2 mb-4">
          <h1 className="flex text-2xl sm:text-3xl font-bold text-gray-800 gap-x-2">
            <span className="text-blue-700">Battery</span>
            <span className="text-blue-500">Stats</span>
          </h1>
        </div>

        <div className="grid grid-cols-2 gap-8 mb-4 border-b border-gray-300 pb-4">
          <div className="flex items-center gap-3">
            <label className="text-xs font-medium text-gray-700 w-16">
              State
            </label>
            <select
              className="border border-gray-400 px-2 py-1 text-xs flex-1"
              value={stateFilter}
              onChange={(e) => setStateFilter(e.target.value)}
              disabled={loadingStates}
            >
              {states.length > 0 ? (
                states.map((s) => (
                  <option key={s.id} value={s.value}>
                    {s.name}
                  </option>
                ))
              ) : (
                <option>No states</option>
              )}
            </select>
          </div>

          <div className="flex items-center gap-3">
            <label className="text-xs font-medium text-gray-700 w-16">
              Division
            </label>
            <select
              className="border border-gray-400 px-2 py-1 text-xs flex-1"
              value={divisionFilter}
              onChange={(e) => setDivisionFilter(e.target.value)}
              disabled={loadingDivisions || !stateFilter}
            >
              {divisions.length > 0 ? (
                divisions.map((d) => (
                  <option key={d.id} value={d.value}>
                    {d.name}
                  </option>
                ))
              ) : (
                <option>No divisions</option>
              )}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8 mb-4">
          <div className="flex items-center gap-3">
            <label className="text-xs font-medium text-gray-700 w-24">
              Working Date
            </label>
            <input
              type="date"
              className="border border-gray-400 px-2 py-1 text-xs"
              value={workingDateIso}
              onChange={(e) => setWorkingDateIso(e.target.value)}
            />
          </div>

          <div className="flex items-center">
            <CustomButton
              onClick={handleGo}
              disabled={loadingEmployees || !stateFilter}
              variant="go"
            >
              {loadingEmployees ? "Loading..." : "GO"}
            </CustomButton>
          </div>
        </div>
      </div>

      <div className="p-4">
        <div className="mb-2 flex justify-center">
          <h2 className="text-xs font-bold text-gray-800">
            {stateFilter} - {divisionFilter || "Division"} -{" "}
            {formatDateForDisplay(workingDateIso)}
          </h2>
        </div>

        <div className="employee-table-wrapper">
          <div className="border rounded shadow  table-wrapper">
            <table className="unlock-dcr-leave-table text-sm ">
              <thead className="bg-blue-500 text-white">
                <tr className="bg-cyan-300">
                  {[
                    "S.No",
                    "Emp Id",
                    "Employee",
                    "Working Date",
                    "Mobile Login",
                    "Mobile Logout",
                    "Latest Level",
                    "Charging",
                    "USB Charging",
                    "AC Charging",
                    "UER Charging",
                    "Device ID",
                    "Designation",
                    "DOJ",
                    "Mobile",
                    "State",
                    "HQ",
                    "Division",
                    "Manager",
                  ].map((h) => (
                    <th key={h} className="border px-2 py-1">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {paginatedRows.length > 0 ? (
                  paginatedRows.map((employee) => (
                    <tr key={employee.bid} className="even:bg-gray-100">
                      <td className="border px-2 py-1">{employee.sno}</td>
                      <td className="border px-2 py-1">{employee.employee}</td>
                      <td className="border px-2 py-1">
                        <button
                          className="text-blue-700 underline"
                          onClick={() => openBatteryStatsForEmployee(employee)}
                        >
                          {employee.name}
                        </button>
                      </td>
                      <td className="border px-2 py-1">
                        {formatDateForDisplay(workingDateIso)}
                      </td>
                      <td className="border px-2 py-1">
                        {formatDateTimeForDisplay(employee.mobileLogin)}
                      </td>
                      <td className="border px-2 py-1">
                        {formatDateTimeForDisplay(employee.mobileLogout)}
                      </td>
                      <td className="border px-2 py-1 text-center">
                        {employee.latest_battery_level ?? "-"}
                      </td>
                      <td className="border px-2 py-1 text-center">
                        {formatCharge(employee.charging)}
                      </td>
                      <td className="border px-2 py-1 text-center">
                        {formatCharge(employee.usb_charging)}
                      </td>
                      <td className="border px-2 py-1 text-center">
                        {formatCharge(employee.ac_charging)}
                      </td>
                      <td className="border px-2 py-1 text-center">
                        {formatCharge(employee.uerCharging)}
                      </td>
                      <td className="border px-2 py-1">{employee.deviceId}</td>
                      <td className="border px-2 py-1">
                        {employee.designation}
                      </td>
                      <td className="border px-2 py-1">
                        {formatDateForDisplay(employee.final_joining_date)}
                      </td>
                      <td className="border px-2 py-1">{employee.phone}</td>
                      <td className="border px-2 py-1">{employee.state}</td>
                      <td className="border px-2 py-1">{employee.hq}</td>
                      <td className="border px-2 py-1">{employee.division}</td>
                      <td className="border px-2 py-1">{employee.manager}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="19" className="text-center py-3">
                      No Records Found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {employees.length > 50 && (
          <div className="flex justify-center gap-3 py-4">
            <button
              disabled={page === 1}
              className="px-3 py-1 border rounded"
              onClick={() => setPage(page - 1)}
            >
              Prev
            </button>
            <span className="px-4 py-1 border rounded">
              Page {page} / {Math.ceil(employees.length / rowsPerPage)}
            </span>
            <button
              disabled={page >= Math.ceil(employees.length / rowsPerPage)}
              className="px-3 py-1 border rounded"
              onClick={() => setPage(page + 1)}
            >
              Next
            </button>
          </div>
        )}

        <div className="mt-4 flex gap-4 justify-center">
          <CustomButton onClick={handlePrint} variant="print">
            🖨 Print
          </CustomButton>
          <CustomButton onClick={handleExport} variant="export">
            📥 Export
          </CustomButton>
        </div>
      </div>
    </div>
  );
}
